import { fetchAuthSession } from 'aws-amplify/auth'
import { db } from '@/db/db'
import { apiService } from 'services/ApiService'
import type { DiagnosticEvent } from 'tsp-types'

export const DIAGNOSTIC_EVENTS_BATCH_MAX = 100
export const OUTBOX_FLUSH_INTERVAL_MS = 30_000
export const OUTBOX_FLUSH_DEBOUNCE_MS = 2_000
export const OUTBOX_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1_000 // 7 days

// Backoff schedule (ms): 2s, 30s, 2m, 8m, 32m, 2h — then cap at 4h
const BACKOFF_SCHEDULE_MS = [
  2_000, 30_000, 120_000, 480_000, 1_920_000, 7_200_000,
]
const BACKOFF_CAP_MS = 4 * 60 * 60 * 1_000

export function backoffMs(attempts: number): number {
  return BACKOFF_SCHEDULE_MS[attempts] ?? BACKOFF_CAP_MS
}

/**
 * A 4xx response (validation error, malformed event) is permanent — retrying
 * it can never succeed, so it must not block later, valid events. Anything
 * else (network error with no response, 5xx) is treated as retryable.
 */
export function isPermanentFailure(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false
  const status = (err as { response?: { status?: number } }).response?.status
  return typeof status === 'number' && status >= 400 && status < 500
}

export async function getStalePendingEventCount(
  staleSinceMs = 60_000
): Promise<number> {
  const cutoff = Date.now() - staleSinceMs
  return db.pendingEvents.where('createdAt').below(cutoff).count()
}

export class DiagnosticEventOutbox {
  private readonly inFlight = new Set<string>()
  private readonly debounceTimers = new Map<
    string,
    ReturnType<typeof setTimeout>
  >()
  private backgroundInterval: ReturnType<typeof setInterval> | null = null
  /** Cached auth token from the last successful flush, used by flushOnUnload. */
  private cachedToken: string | null = null
  /** In-memory mirror of pending events per session for synchronous access in flushOnUnload. */
  private readonly pendingBySession = new Map<string, DiagnosticEvent[]>()

  /**
   * Track an event in the in-memory mirror without writing to Dexie.
   * Used by applyAndPersist, which writes the Dexie row directly.
   * Populates pendingBySession so flushOnUnload can send events synchronously.
   */
  trackEvent(sessionId: string, event: DiagnosticEvent): void {
    const arr = this.pendingBySession.get(sessionId) ?? []
    arr.push(event)
    this.pendingBySession.set(sessionId, arr)
  }

  async enqueue(event: DiagnosticEvent): Promise<void> {
    await db.pendingEvents.add({
      sessionId: event.sessionId,
      event,
      createdAt: Date.now(),
      attempts: 0,
      nextRetryAt: Date.now(),
    })
    const arr = this.pendingBySession.get(event.sessionId) ?? []
    arr.push(event)
    this.pendingBySession.set(event.sessionId, arr)
    this.scheduleFlush(event.sessionId)
  }

  scheduleFlush(sessionId: string): void {
    const existing = this.debounceTimers.get(sessionId)
    if (existing !== undefined) clearTimeout(existing)
    const id = setTimeout(() => {
      this.debounceTimers.delete(sessionId)
      void this.flush(sessionId)
    }, OUTBOX_FLUSH_DEBOUNCE_MS)
    this.debounceTimers.set(sessionId, id)
  }

  async flush(sessionId: string): Promise<void> {
    if (this.inFlight.has(sessionId)) return
    this.inFlight.add(sessionId)
    try {
      const now = Date.now()
      const cutoff = now - OUTBOX_MAX_AGE_MS

      // Stale rows are abandoned — delete them outright so they can never
      // block a later, still-live event from being sent (mirrors flushAll's
      // cross-session prune, scoped to this session).
      await db.pendingEvents
        .where('sessionId')
        .equals(sessionId)
        .filter(r => r.createdAt < cutoff)
        .delete()

      const rows = await db.pendingEvents
        .where('sessionId')
        .equals(sessionId)
        .sortBy('createdAt')

      // Ordered prefix: stop at the first row still backing off so a later
      // event can never be sent ahead of one that's still waiting to retry.
      const eligible: typeof rows = []
      for (const row of rows) {
        if (row.nextRetryAt > now) break
        eligible.push(row)
      }
      if (eligible.length === 0) return

      for (let i = 0; i < eligible.length; i += DIAGNOSTIC_EVENTS_BATCH_MAX) {
        const batch = eligible.slice(i, i + DIAGNOSTIC_EVENTS_BATCH_MAX)
        const events = batch.map(r => r.event)
        const { projectId } = events[0]
        try {
          await apiService.post(
            `/projects/${projectId}/sessions/${sessionId}/diagnostic-events`,
            { events },
            {
              tokenGenerator: async () => {
                const session = await fetchAuthSession()
                const token = session.tokens?.idToken?.toString() ?? ''
                this.cachedToken = token
                return token
              },
            }
          )
          await db.pendingEvents.bulkDelete(batch.map(r => r.id!))
          this.pendingBySession.delete(sessionId)
          if (import.meta.env.DEV) {
            void (async () => {
              try {
                const [
                  { snapshotCache },
                  { fetchBackendSnapshot, diffSnapshots, logDrift },
                ] = await Promise.all([
                  import('services/DiagnosticEventService'),
                  import('lib/diagnosticDriftDetector'),
                ])
                const frontendSnap = snapshotCache.get(sessionId)
                if (!frontendSnap) return
                const backendSnap = await fetchBackendSnapshot(
                  sessionId,
                  projectId
                )
                if (!backendSnap) return
                logDrift(diffSnapshots(sessionId, frontendSnap, backendSnap))
              } catch {
                // Drift detection is best-effort; ignore errors silently
              }
            })()
          }
        } catch (err) {
          if (isPermanentFailure(err)) {
            // Permanently invalid — drop it so it can never block later,
            // valid events. Retrying it would fail forever anyway.
            console.warn(
              'DiagnosticEventOutbox: dropping permanently-rejected events',
              {
                sessionId,
                eventIds: batch.map(r => r.event.eventId),
                eventTypes: batch.map(r => r.event.eventType),
              }
            )
            await db.pendingEvents.bulkDelete(batch.map(r => r.id!))
          } else {
            await db.pendingEvents.bulkPut(
              batch.map(r => ({
                ...r,
                attempts: r.attempts + 1,
                nextRetryAt: Date.now() + backoffMs(r.attempts + 1),
              }))
            )
            // Retryable — later batches must wait for this one, preserving
            // per-session delivery order.
            break
          }
        }
      }
    } finally {
      this.inFlight.delete(sessionId)
    }
  }

  async flushAll(): Promise<void> {
    const cutoff = Date.now() - OUTBOX_MAX_AGE_MS
    await db.pendingEvents.where('createdAt').below(cutoff).delete()
    const sessionIds = (await db.pendingEvents
      .orderBy('sessionId')
      .uniqueKeys()) as string[]
    await Promise.all(sessionIds.map(sid => this.flush(sid)))
  }

  startBackground(): void {
    if (this.backgroundInterval !== null) clearInterval(this.backgroundInterval)
    this.backgroundInterval = setInterval(() => {
      void this.flushAll()
    }, OUTBOX_FLUSH_INTERVAL_MS)
  }

  stopBackground(): void {
    if (this.backgroundInterval !== null) {
      clearInterval(this.backgroundInterval)
      this.backgroundInterval = null
    }
  }

  /**
   * Best-effort delivery on page unload. Uses a keepalive fetch (survives
   * page close) with the auth token cached from the last successful flush.
   * No-op if no token is cached yet (i.e. no flush has succeeded this session).
   */
  flushOnUnload(sessionId: string): void {
    if (!this.cachedToken) return
    const events = this.pendingBySession.get(sessionId) ?? []
    if (events.length === 0) return
    const { projectId } = events[0]
    void fetch(
      `/projects/${projectId}/sessions/${sessionId}/diagnostic-events`,
      {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.cachedToken}`,
        },
        body: JSON.stringify({ events }),
      }
    )
  }
}

export const outbox = new DiagnosticEventOutbox()
